import type { MouseEvent } from 'react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { useCollapsedRows } from 'hooks/useCollapsedRows';
import { useIsomorphicLayoutEffect } from 'hooks/useIsomorphicLayoutEffect';
import useMedia from 'hooks/useMedia';
import { createTestId } from 'utils/createTestId';
import type { WithTestId } from 'types/app';

import { Button } from '@Atomic/atoms/Button';
import { Icons } from '@Atomic/atoms/Icons';
import { LinkRouter } from '@Atomic/atoms/LinkRouter';

import styles from './categoryLinks.module.scss';

export type CategoryLinkItem = {
  id: number;
  key: string;
  title: string;
  count: number;
  to: string;
};

export type CategoryLinkClickMeta = {
  /** 1-based, counted in the full list — hidden items included. */
  position: number;
  isExpanded: boolean;
  /** «Показать ещё» was offered at some point for this strip instance. */
  hadToggle: boolean;
};

export type CategoryLinksShowMoreMeta = {
  visibleCount: number;
  totalCount: number;
};

export type CategoryLinksListProps = WithTestId<{
  items: CategoryLinkItem[];
  onItemClick?: (item: CategoryLinkItem, meta: CategoryLinkClickMeta) => void;
  onItemSelect?: (categoryId: number) => void;
  onShowMoreClick?: (meta: CategoryLinksShowMoreMeta) => void;
  /** Defaults to `!xs`: phones keep the scroller. */
  collapsible?: boolean;
  className?: string;
}>;

/** Mirrored by the `.collapsed` pre-measure clamp in the SCSS — change both together. */
const MAX_ROWS = 3;

const ITEM_SELECTOR = '[data-row-item]';
const TOGGLE_SELECTOR = '[data-row-toggle]';

export const CategoryLinksList = ({
  items,
  onItemClick,
  onItemSelect,
  onShowMoreClick,
  collapsible,
  className,
  testId = 'filters_category',
}: CategoryLinksListProps) => {
  const { t } = useTranslation();
  const { xs } = useMedia();
  const containerRef = useRef<HTMLDivElement>(null);
  const containerId = useId();
  const getTestId = createTestId(testId);
  // Resets only on remount: the container keys the list by item ids.
  const [expanded, setExpanded] = useState(false);
  const hadToggleRef = useRef(false);
  const revealFromIndexRef = useRef<number | null>(null);

  const isCollapsed = (collapsible ?? !xs) && !expanded;
  // Same ids with new counters/titles re-wrap the rows without changing the count.
  const contentKey = useMemo(
    () => items.map((item) => `${item.id}:${item.title}:${item.count}`).join('|'),
    [items]
  );
  const { visibleCount, hasOverflow } = useCollapsedRows({
    containerRef,
    itemCount: items.length,
    contentKey,
    maxRows: MAX_ROWS,
    enabled: isCollapsed,
    itemSelector: ITEM_SELECTOR,
    toggleSelector: TOGGLE_SELECTOR,
  });

  // `hasOverflow` goes false once the clamp is off and again while a re-measure (resize, late font
  // swap, new counters) is pending, both with the toggle still on screen — so latch it.
  useIsomorphicLayoutEffect(() => {
    hadToggleRef.current = hadToggleRef.current || expanded || hasOverflow;
  }, [expanded, hasOverflow]);

  useEffect(() => {
    const index = revealFromIndexRef.current;

    if (!expanded || index === null) {
      return;
    }

    revealFromIndexRef.current = null;
    // The toggle unmounts on expand; keep keyboard users on the first revealed link.
    containerRef.current
      ?.querySelectorAll<HTMLElement>(ITEM_SELECTOR)
      [index]?.focus({ preventScroll: true });
  }, [expanded]);

  if (items.length === 0) {
    return null;
  }

  const handleItemClick =
    (item: CategoryLinkItem, index: number) => (event: MouseEvent<HTMLAnchorElement>) => {
      onItemClick?.(item, {
        position: index + 1,
        isExpanded: expanded,
        hadToggle: hadToggleRef.current,
      });

      if (onItemSelect) {
        event.preventDefault();
        onItemSelect(item.id);
      }
    };

  const handleExpand = () => {
    // Unmeasured (toggle still invisible): expand, but report no made-up count.
    if (visibleCount !== null) {
      onShowMoreClick?.({ visibleCount, totalCount: items.length });
    }
    revealFromIndexRef.current = visibleCount;
    setExpanded(true);
  };

  const isMeasured = visibleCount !== null;
  // Rendered invisible before the first measurement so its width can be read.
  const showToggle = isCollapsed && (!isMeasured || hasOverflow);

  return (
    <div
      ref={containerRef}
      id={containerId}
      className={cn(
        styles.container,
        { [styles.collapsed]: isCollapsed, [styles.measured]: isMeasured },
        className
      )}
      data-testid={getTestId('links')}
    >
      {items.map((item, index) => (
        <LinkRouter
          key={item.id}
          data-row-item
          hidden={isCollapsed && isMeasured && index >= visibleCount}
          testId={getTestId('link', item.key)}
          to={item.to}
          className={styles.link}
          onClick={handleItemClick(item, index)}
        >
          {item.title}
          {item.count > 0 && <span className={styles.count}>{item.count}</span>}
        </LinkRouter>
      ))}
      {showToggle && (
        <Button
          variant="text"
          size="medium"
          data-row-toggle
          className={cn(styles.showMore, { [styles.pending]: !isMeasured })}
          iconRight={<Icons.ChevronDown />}
          aria-expanded={false}
          aria-controls={containerId}
          onClick={handleExpand}
          testId={getTestId('show-more')}
        >
          {t('buttons.showMore')}
        </Button>
      )}
    </div>
  );
};
